import { create } from 'zustand';
import { User, AuthState } from '../types/user';
import axios from 'axios';

// Define the API URL
const API_URL = process.env.REACT_APP_API_URL || '/api';

interface AuthStore extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  resetPassword: (email: string) => Promise<void>;
  verifyResetToken: (token: string) => Promise<any>;
  confirmPasswordReset: (token: string, password: string) => Promise<void>;
  initializeFromStorage: () => void;
}

const useAuthStore = create<AuthStore>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,

  login: async (email: string, password: string) => {
    try {
      set({ isLoading: true, error: null });
      
      // Call the backend API for authentication
      const response = await axios.post(`${API_URL}/auth/login`, { email, password });
      
      if (response.data.success) {
        const { user, token } = response.data;
        
        // Store token in localStorage for persistence
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        
        set({ user, isAuthenticated: true, isLoading: false });
      } else {
        throw new Error(response.data.message || 'Login failed');
      }
    } catch (error: any) {
      set({ 
        isLoading: false, 
        error: error.response?.data?.message || error.message || 'An unknown error occurred' 
      });
    }
  },

  register: async (name: string, email: string, password: string) => {
    try {
      set({ isLoading: true, error: null });
      
      // Call the backend API for registration
      const response = await axios.post(`${API_URL}/auth/register`, { 
        name, 
        email, 
        password 
      });
      
      if (response.data.success) {
        const { user, token } = response.data;
        
        // Store token in localStorage
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        
        set({ user, isAuthenticated: true, isLoading: false });
      } else {
        throw new Error(response.data.message || 'Registration failed');
      }
    } catch (error: any) {
      set({ 
        isLoading: false, 
        error: error.response?.data?.message || error.message || 'An unknown error occurred' 
      });
    }
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    set({ user: null, isAuthenticated: false });
  },

  resetPassword: async (email: string) => {
    try {
      set({ isLoading: true, error: null });
      
      // Call the backend API for password reset
      const response = await axios.post(`${API_URL}/auth/request-password-reset`, { email });
      
      if (response.data.success) {
        set({ isLoading: false });
        console.log(`Password reset request sent for: ${email}`);
      } else {
        throw new Error(response.data.message || 'Password reset request failed');
      }
    } catch (error: any) {
      set({ 
        isLoading: false, 
        error: error.response?.data?.message || error.message || 'An unknown error occurred' 
      });
      throw error;
    }
  },

  verifyResetToken: async (token: string) => {
    try {
      set({ isLoading: true, error: null });
      
      // Call the backend API to verify the reset token
      const response = await axios.get(`${API_URL}/auth/verify-reset-token/${token}`);
      
      if (response.data.success) {
        set({ isLoading: false });
        return response.data.user;
      } else {
        throw new Error(response.data.message || 'Invalid or expired token');
      }
    } catch (error: any) {
      set({ 
        isLoading: false, 
        error: error.response?.data?.message || error.message || 'Token verification failed' 
      });
      throw error;
    }
  },

  confirmPasswordReset: async (token: string, password: string) => {
    try {
      set({ isLoading: true, error: null });
      
      // Call the backend API to reset the password
      const response = await axios.post(`${API_URL}/auth/reset-password/${token}`, { password });
      
      if (response.data.success) {
        set({ isLoading: false });
        console.log('Password reset successfully');
      } else {
        throw new Error(response.data.message || 'Password reset failed');
      }
    } catch (error: any) {
      set({ 
        isLoading: false, 
        error: error.response?.data?.message || error.message || 'Password reset failed' 
      });
      throw error;
    }
  },

  initializeFromStorage: async () => {
    try {
      console.log('🔍 Initializing auth from storage...');
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      console.log('🔍 Storage check:', {
        hasToken: !!token,
        hasUser: !!storedUser,
        tokenLength: token?.length || 0
      });
      
      if (token && storedUser) {
        const user = JSON.parse(storedUser) as User;
        console.log('🔍 Parsed user from storage:', {
          id: user.id,
          name: user.name,
          email: user.email,
          role: user.role
        });
        
        // Set up axios to use the token for future requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Validate that the user object has the required fields
        if (user && user.id && user.email && user.name && user.role) {
          // Validate token with server to ensure it's still valid
          try {
            console.log('🔍 Validating token with server...');
            const response = await axios.get(`${API_URL}/auth/validate-token`);
            console.log('✅ Token validation successful:', response.data);
            set({ user, isAuthenticated: true });
            console.log('✅ User loaded from localStorage and token validated:', user.name);
          } catch (tokenError: any) {
            console.warn('❌ Token validation failed:', tokenError.response?.status, tokenError.response?.data);
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            delete axios.defaults.headers.common['Authorization'];
            set({ user: null, isAuthenticated: false });
          }
        } else {
          // Invalid user data, clear it
          console.warn('❌ Invalid user data in localStorage');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      } else {
        console.log('ℹ️ No token or user data in localStorage');
        set({ user: null, isAuthenticated: false });
      }
    } catch (error) {
      console.error('❌ Error loading user from localStorage:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      set({ user: null, isAuthenticated: false });
    }
  }
}));

// Setup axios interceptor to add the auth token to all requests
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Initialize auth state from localStorage when the module is imported
useAuthStore.getState().initializeFromStorage();

export default useAuthStore; 